import StatusChip from 'components/@extended/StatusChip';
import { RightOutlined } from 'components/icons';
import MatterIndicators from 'components/matters/MatterIndicators';
import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import React from 'react';
import { ClassifierData } from 'types/classifiers';
import { AddressedTaskListItem, getPriorityStatusChipType } from 'types/tasks';
import { cn } from 'utils/cn';
import { getClassifierText, t } from 'utils/getTranslations';

interface AddressedTaskProps {
    addressedTask: AddressedTaskListItem;
    openAddressedTaskView: (taskId: number) => void;
    classifiers: Record<string, ClassifierData>;
}

const PERSON_LINK_CLASS = 'text-link cursor-pointer underline';
const ROW_CLASS = 'flex w-full gap-4 px-6 py-4';
const CLICKABLE_ROW_CLASS =
    'hover:bg-grey-100 focus-visible:bg-grey-100 focus-visible:ring-ring/50 cursor-pointer text-left outline-none focus-visible:ring-3 focus-visible:ring-inset';

const AddressedTaskItem: React.FC<AddressedTaskProps> = ({ addressedTask, openAddressedTaskView, classifiers }) => {
    const { name, taskTypeCodes, isAutomatic, clientParties } = addressedTask;
    const firstClientParty = clientParties && clientParties.length > 0 ? clientParties[0] : null;
    const types = (taskTypeCodes || [])
        .map((code) => getClassifierText(classifiers['TASK_TYPE'], code))
        .filter((item) => !!item)
        .join(', ');

    const details = (
        <div className="flex w-[90%] flex-col gap-1">
            <p className="font-semibold">
                {types}
                {types && ', '}
                {isAutomatic && firstClientParty ? (
                    <>
                        {name} <PartyName party={firstClientParty} isLink variant="readonly-other" />
                    </>
                ) : (
                    name
                )}
            </p>
            {isAutomatic && firstClientParty && (
                <>
                    {firstClientParty.relationshipPartners && firstClientParty.relationshipPartners.length > 0 && (
                        <p className="text-grey-600">
                            {t('contacts.relationship-partners')}:{' '}
                            {firstClientParty.relationshipPartners.map((partner, index) => (
                                <React.Fragment key={partner.id}>
                                    {index > 0 && ', '}
                                    <RecordLink
                                        to={`/employee/${partner.id}`}
                                        className={PERSON_LINK_CLASS}
                                        label={partner.name}
                                    />
                                </React.Fragment>
                            ))}
                        </p>
                    )}
                    {firstClientParty.relationshipManagers && firstClientParty.relationshipManagers.length > 0 && (
                        <p className="text-grey-600">
                            {t('contacts.relationship-managers')}:{' '}
                            {firstClientParty.relationshipManagers.map((manager, index) => (
                                <React.Fragment key={manager.id}>
                                    {index > 0 && ', '}
                                    <RecordLink
                                        to={`/employee/${manager.id}`}
                                        className={PERSON_LINK_CLASS}
                                        label={manager.name}
                                    />
                                </React.Fragment>
                            ))}
                        </p>
                    )}
                </>
            )}
            {!isAutomatic && firstClientParty && (
                <p className="text-grey-400">
                    <PartyName party={firstClientParty} variant="readonly-other" />
                </p>
            )}
            {addressedTask.matters &&
                addressedTask.matters.map((matter) => (
                    <p key={matter.id} className="flex items-center gap-1">
                        <MatterIndicators isConfidential={matter.isConfidential} />
                        {matter.name}
                    </p>
                ))}
            <div className="flex flex-nowrap">
                <StatusChip
                    type={getPriorityStatusChipType(addressedTask.taskPriorityTypeCode)}
                    label={getClassifierText(classifiers['TASK_PRIORITY_TYPE'], addressedTask.taskPriorityTypeCode)}
                />
            </div>
        </div>
    );

    return (
        <li className="border-divider border-b">
            {isAutomatic ? (
                <div className={ROW_CLASS}>{details}</div>
            ) : (
                <button
                    type="button"
                    className={cn(ROW_CLASS, CLICKABLE_ROW_CLASS)}
                    onClick={() => openAddressedTaskView(addressedTask.id)}
                >
                    {details}
                    <div className="flex flex-col justify-center">
                        <RightOutlined className="text-primary ml-2.5 text-xs" />
                    </div>
                </button>
            )}
        </li>
    );
};

export default AddressedTaskItem;
